import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/utils/api";
import { fmtCurrency, fmtDate } from "@/utils/format";
import { B2B } from "@/constants/testIds";
import { TrendingUp, TrendingDown, ChevronLeft, ChevronRight } from "lucide-react";

const PER_PAGE = 20;

export default function B2BEkstre() {
  const { user } = useAuth();
  const [cari, setCari] = useState(null);
  const [hareketler, setHareketler] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const loadData = useCallback(async () => {
    if (!user?.cari_id) return;
    setLoading(true);
    setError(false);
    try {
      const [cariRes, hareketRes] = await Promise.all([
        api.get(`/cariler/${user.cari_id}`),
        api.get("/cari-hareketler", { params: { cari_id: user.cari_id, page, per_page: PER_PAGE } }),
      ]);
      setCari(cariRes.data);
      setHareketler(hareketRes.data.items);
      setTotal(hareketRes.data.total);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-lg font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>Cari Ekstre</h1>

      <p className="text-sm text-slate-500">Siparişlerinizden ayrı hesap hareketleri. Wolvox ekstre eşleştirmesi henüz etkin değil.</p>
      {cari && <p className="font-medium text-slate-700">{cari.unvan}</p>}
      {error && <p role="alert" className="text-red-600">Ekstre yüklenemedi. <button onClick={loadData} className="underline">Tekrar dene</button></p>}

      {/* Movements Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-800">Hesap Hareketleri ({total})</div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-7 h-7 border-4 border-[#0D1B3E] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div data-testid={B2B.ekstreTable} hidden={error}>
            {hareketler.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm">Hareket bulunamadı</div>
            ) : hareketler.map((h) => (
              <div key={h.id} className="flex items-center justify-between px-4 py-3.5 border-b border-slate-50 last:border-0">
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${h.borc > 0 ? "bg-red-50" : "bg-green-50"}`}>
                    {h.borc > 0
                      ? <TrendingUp size={14} className="text-red-500" />
                      : <TrendingDown size={14} className="text-green-500" />
                    }
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-800 leading-snug">{h.aciklama}</div>
                    {h.belge_no && <div className="text-xs text-slate-400 font-mono">{h.belge_no}</div>}
                    <div className="text-xs text-slate-400">{fmtDate(h.tarih)}</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {h.borc > 0 && (
                    <div className="text-sm font-bold text-red-600 tabular-nums">{fmtCurrency(h.borc)}</div>
                  )}
                  {h.alacak > 0 && (
                    <div className="text-sm font-bold text-green-600 tabular-nums">{fmtCurrency(h.alacak)}</div>
                  )}
                  <div className="text-xs text-slate-400 capitalize">{h.kaynak || "-"}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <span className="text-xs text-slate-500">{total} hareket</span>
          <div className="flex items-center gap-1">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="p-1.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40">
              <ChevronLeft size={15} />
            </button>
            <span className="text-xs text-slate-600 px-2">Sayfa {page}</span>
            <button disabled={hareketler.length < PER_PAGE} onClick={() => setPage((p) => p + 1)}
              className="p-1.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40">
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
